import React, { useState } from 'react'
import { Image } from 'primereact/image';
import { Slider } from 'primereact/slider';
import { Message } from 'primereact/message';
import { IPackageItem } from 'pages/Packages/View/api';
import { getTaskLabelByType } from 'definitions';
import { Button } from 'primereact/button';
import { Chip } from 'primereact/chip';
import { Tooltip } from 'primereact/tooltip';
import defaultImage from './default-image.png';

interface Props {
  packageItem: IPackageItem & { accepted: boolean, rejected: boolean, accepting: boolean, rejecting: boolean },
  showPriceInformation?: boolean,
  showSalesInformation?: boolean,
  onAcceptItem?: (acceptedPrice: number) => any,
  onRejectItem?: () => any,
  onItemPriceChanged?: (value: number) => any
}

export const PackageItem = ({
  packageItem,
  showPriceInformation,
  showSalesInformation,
  onAcceptItem,
  onRejectItem,
  onItemPriceChanged
}: Props) => {
  const {
    accepting,
    rejecting,
    accepted,
    rejected,
    suggestedPrice = 0,
    price = 0,
    grossSellerEarning = 0,
    minChangeablePrice,
    maxChangeablePrice
  } = packageItem
  const [showPriceSlider, setShowPriceSlider] = useState<boolean>(false);
  const showDecisionControls = !accepted && !rejected && !!(onAcceptItem || onRejectItem);
  const showPriceChangeControls = !accepted && !rejected && !!onAcceptItem && !!onItemPriceChanged;

  const handleAcceptOffer = () => {
    if (onAcceptItem) onAcceptItem(price);
  };
  const handleRejectOffer = () => {
    if (onRejectItem) onRejectItem();
  };

  const displayImage = packageItem.images[0]

  const priceRange = maxChangeablePrice - minChangeablePrice;

  const priceSliderValue = ((price - minChangeablePrice) / priceRange) * 100;
  const handlePriceSliderChanged = (value: number | [number, number]) => {
    if (!onItemPriceChanged) return;
    const destValue = Array.isArray(value) ? value[0] : value;
    onItemPriceChanged(
      Math.round(minChangeablePrice + (destValue * priceRange / 100))
    );
  }
  const handleCancelPriceChange = () => {
    if (!onItemPriceChanged) return;
    onItemPriceChanged(suggestedPrice);
    setShowPriceSlider(false);
  }

  const quantityMultiplier = packageItem.sellQuantity > 1 ? (
    <span className='text-xs text-gray-600'> X {packageItem.sellQuantity} </span>
  ) : null

  return (
    <div className="col-12 py-3">
      <div className="flex align-items-center">
        <div className='mr-3'>
          <Image
            src={displayImage || defaultImage}
            width="64"
            height="64"
            preview={!!displayImage}
            imageStyle={{ objectFit: 'cover', border: "1px solid #cccccc" }}
          />
        </div>
        <div>
          <div className='font-bold'>{packageItem.name}</div>
          <div className='mt-1 text-sm'><span className='font-bold'>Code:</span> {packageItem.code}</div>
          {packageItem.sellQuantity > 1 ? (
            <div className='mt-1 text-sm'><span className='font-bold'>Quantity:</span> {packageItem.sellQuantity}</div>
          ): null}
        </div>
      </div>
      {showPriceInformation ? (
        <div className="mt-3">
          {
            packageItem.price ? (
              <>
                <div className="flex justify-content-between align-items-center">
                  <div>
                    <span className="font-semibold">Sell Price:</span> {price} {quantityMultiplier}
                  </div>
                  <div>
                    <span className="font-semibold">Your Earning:</span> {grossSellerEarning} {quantityMultiplier}
                  </div>
                </div>
                {packageItem?.repairs.length > 0 ? (
                  <div className="my-2 text-sm">
                    <span className="font-semibold">Extras:</span>
                    {packageItem.repairs.map((repair, i, arr) => (
                      <span key={repair.type} className={`ml-2 repair-${packageItem._id}-${repair.type} cursor-pointer`}>
                        <i className="pi pi-info-circle text-sm"></i> {' '}
                        {getTaskLabelByType(repair.type)}
                        <Tooltip target={`.repair-${packageItem._id}-${repair.type}`} position="bottom">
                          {repair.note || "This makes items more attractive to buyers"}
                        </Tooltip>
                      </span>
                    ))}
                  </div>
                ) : null}
                {accepted ? (
                  <div className="mt-3 text-right"> <Chip label="Accepted" icon="pi pi-check" /> </div>
                ) : null}
                {rejected ? (
                  <div className="mt-3 text-right"> <Chip label="Declined" icon="pi pi-times" /> </div>
                ) : null}
                {showPriceChangeControls ? (
                  <div className='my-3'>
                    <Message
                      className='w-full justify-content-start align-items-center pl-3 cursor-pointer'
                      onClick={showPriceSlider ? undefined : () => setShowPriceSlider(true)}
                      content={
                        showPriceSlider ? (
                          <>
                            <div className='py-1 flex-1'>
                              <div className='mb-2 text-sm'>Move the slider to adjust sell price</div>
                              <Slider step={calculateReasonableStep(priceRange)} value={priceSliderValue} onChange={v => handlePriceSliderChanged(v.value)} />
                            </div>
                            <div className='ml-2'>
                              <Button onClick={handleCancelPriceChange} icon="pi pi-times" className="p-button-info p-button-text p-button-sm" aria-label="Cancel" />
                            </div>
                          </>
                        ) : (
                          <>
                            <span className="ml-2 text-sm text-cyan-700 flex-1"> Don't agree with the price? </span>
                            <Chip className='text-sm bg-cyan-100' label="Adjust Price" />
                          </>
                        )
                      }
                    />
                  </div>
                ) : null}
                {showDecisionControls ? (
                  <div className="mt-3 text-right">
                    {
                      !!onRejectItem ? (
                        <Button
                          label="Decline"
                          icon="pi pi-times"
                          className="p-button-danger p-button-sm p-button-outlined"
                          onClick={handleRejectOffer}
                          disabled={accepting}
                          loading={rejecting}
                        />
                      ) : null
                    }
                    {
                      !!onAcceptItem ? (
                        <Button
                          label="Accept"
                          icon="pi pi-check"
                          className="p-button-success p-button-sm ml-2"
                          onClick={handleAcceptOffer}
                          loading={accepting}
                          disabled={rejecting}
                        />
                      ) : null
                    }
                  </div>
                ) : null}
              </>
            ) : (
              <p>Sell Price: -</p>
            )
          }
        </div>
      ) : null}
      {showSalesInformation ? (
        <div className='mt-3 flex gap-2 align-items-center'>
          <SubStatusChip label='Live In Shop' count={packageItem.sellQuantity - packageItem.bookedQuantity - packageItem.soldQuantity} />
          <SubStatusChip label='Booked' count={packageItem.bookedQuantity} />
          <SubStatusChip label='Sold' count={packageItem.soldQuantity} />
        </div>  
      ) : null}
    </div>
  )
}

function SubStatusChip ({ label, count }: { label: string, count: number }) {
  if (count === 0) return null;
  return (
    <Chip className='text-sm' label={count > 1 ? `${count} ${label}` : label} />
  )
}

function calculateReasonableStep (range: number) {
  const stepValue = [5, 10, 15, 20, 25, 50, 100, 150, 200, 250, 300, 350, 400, 450, 500, 600, 650, 700, 750, 800, 900, 1000].find(v => (range / v) <= 100)
  return stepValue ? (stepValue / range * 100) : 1;
}
